import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, type SaasAccount, type AuthSession, type WorkspaceAccess } from '../services/authService';

interface AuthContextType {
  // Auth state
  session: AuthSession | null;
  account: SaasAccount | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Workspace state
  currentWorkspace: WorkspaceAccess | null;
  workspaces: WorkspaceAccess[];
  
  // Auth actions
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  
  // Workspace actions
  switchWorkspace: (organizationId: string) => Promise<void>;
  
  // Helper methods
  getCurrentOrganizationId: () => string;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const existingSession = authService.getSession();
        if (existingSession && authService.isAuthenticated()) {
          setSession(existingSession);
          // TODO: Implement session refresh endpoint
          // For now, just use the existing session if it's not expired
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const signIn = async (email: string, password: string) => {
    const newSession = await authService.signIn({ email, password });
    setSession(newSession);
  };

  const signOut = async () => {
    await authService.signOut();
    setSession(null);
  };

  const refreshSession = async () => {
    // TODO: Implement session refresh endpoint
    throw new Error('Session refresh not implemented yet');
  };

  const switchWorkspace = async (organizationId: string) => {
    const workspace = await authService.switchWorkspace(organizationId);
    // Update session with new workspace
    if (session) {
      const updatedSession = {
        ...session,
        currentWorkspace: workspace
      };
      setSession(updatedSession);
    }
  };

  const getCurrentOrganizationId = (): string => {
    // First try to get org ID from URL - look for org_xxx pattern anywhere in the path
    const urlMatch = window.location.pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
    if (urlMatch && urlMatch[1]) {
      return urlMatch[1];
    }
    
    // Return the organization ID from current workspace, or fallback to demo
    return session?.currentWorkspace?.organizationId || 'org_demo_123456789';
  };

  const value: AuthContextType = {
    // Auth state
    session,
    account: session?.account || null,
    isAuthenticated: !!session && authService.isAuthenticated(),
    isLoading,
    
    // Workspace state
    currentWorkspace: session?.currentWorkspace || null,
    workspaces: session?.workspaces || [],
    
    // Auth actions
    signIn,
    signOut,
    refreshSession,
    
    // Workspace actions
    switchWorkspace,
    
    // Helper methods
    getCurrentOrganizationId,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};